<template>
  <b-container class="mt-3 mb-2">
    <b-card>
      <validation-observer v-slot="{ invalid }" ref="form">
        <b-container class="m-0 p-0 mw-100">
          <b-row>
            <b-col cols="12" sm="12" md="8" lg="8" xl="8">
              <validation-provider :name="$tc('message.fullName', 1).toLowerCase()"
                                   :rules="{ max: 50, required: true }"
                                   v-slot="{ valid, errors }">
                <b-form-group>
                  <label slot="label" class="m-0 p-0">
                    {{$tc('message.fullName', 1)}} (*)
                  </label>

                  <b-input-group>
                    <b-form-input id="input-fullName"
                                  v-model="input.fullName"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: Barberini`"
                                  :state="itemChanged ? valid : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="4" lg="4" xl="4">
              <b-form-group :label="`${$tc('message.type', 1)} ${$tc('message.user', 1).toLowerCase()}`"
                            label-for="user-type">
                <model-list-select id="user-type"
                                   option-text="descr"
                                   option-value="id"
                                   :placeholder="userTypeOptions && userTypeOptions.length > 1 ? $t('message.selectOption') : $t('message.noOption')"
                                   v-model="input.type"
                                   :list="userTypeOptions">
                </model-list-select>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" sm="12" md="8" lg="4" xl="4">
              <validation-provider :name="$tc('message.username', 1).toLowerCase()"
                                   :rules="{ min: 6, max: 20, required: true }"
                                   v-slot="{ valid, errors }">
                <b-form-group>
                  <label slot="label" class="m-0 p-0">
                    {{$tc('message.username', 1)}} (*)
                  </label>

                  <b-input-group>
                    <b-form-input id="input-surname"
                                  v-model="input.userName"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: Barberini`"
                                  :state="itemChanged ? valid : null">
                    </b-form-input>
                  </b-input-group>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <!-- Password -->
              <ValidationProvider :rules="{ required: !userId, min: 6, max: 18 }"
                                  vid="mainPassword"
                                  :name="$tc('message.password', 1).toLowerCase()"
                                  v-slot="{ valid, errors }">
                <b-form-group :label="`${$tc('message.password', 1)} (*)`">
                  <b-form-input id="password"
                                type="password"
                                v-model="input.password"
                                :state="itemChanged ? valid : null">
                  </b-form-input>

                  <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col cols="12" sm="12" md="6" lg="4" xl="4">
              <!-- Password check -->
              <ValidationProvider :rules="{ required: !!input.password, passwordCheck: [passwordCheck, input.password, '', ''] }"
                                  :name="$tc('message.passwordCheck', 1).toLowerCase()"
                                  v-slot="{ valid, errors, changed }">
                <b-form-group :label="`${$tc('message.passwordCheck', 1)} (*)`">
                  <b-form-input id="password-confirm"
                                type="password"
                                v-model="passwordCheck"
                                :state="changed ? valid : null">
                  </b-form-input>

                  <div v-if="changed && !valid" class="my-invalid-feedback"> <h6> {{ first(errors) }}</h6></div>
                </b-form-group>
              </ValidationProvider>
            </b-col>

            <b-col cols="8" sm="8" md="6" lg="4" xl="4">
              <validation-provider :name="$tc('message.role', 1).toLowerCase()"
                                   :rules="{ required: true }"
                                   v-slot="{ changed, valid, errors }">
                <b-form-group :label="`${$tc('message.role', 1)}`"
                              label-for="role">
                  <model-list-select id="role"
                                     option-text="text"
                                     option-value="val"
                                     :disabled="!isAdmin"
                                     :placeholder="roleOptions && roleOptions.length > 1 ? $t('message.selectOption') : $t('message.noOption')"
                                     v-model="input.config.role"
                                     @input="$event !== 'Admin' ? input.config.adminDefault = false : null"
                                     :state="changed ? valid : null"
                                     :list="roleOptions">
                  </model-list-select>

                  <div v-if="changed && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col cols="4" sm="4" md="6" lg="2" xl="2" v-if="input.config.role === 'Admin'">
              <div class="d-flex h-100 w-100 align-items-center">
                <b-form-checkbox v-model="input.config.adminDefault"
                                 v-b-popover.hover.top="`${$tc('message.adminDefault', 1)}`">
                  {{$tc('message.adminDefault', 1)}}
                </b-form-checkbox>
              </div>
            </b-col>

            <b-col cols="12" sm="12" md="6" :lg="input.config.role === 'Admin' && isDefaultAdmin ? 6 : 8" :xl="input.config.role === 'Admin' && isDefaultAdmin ? 6 : 8">
              <validation-provider :name="$tc('message.email', 1).toLowerCase()"
                                   :rules="{ min: 0, max: 50, email: true }"
                                   v-slot="{ valid, errors }">
                <b-form-group>
                  <label slot="label" class="m-0 p-0"> {{$tc('message.email', 1)}} </label>

                  <b-input-group>
                    <b-form-input id="new-mail"
                                  v-model="input.email"
                                  autocomplete="off"
                                  :placeholder="`${$tc('message.example', 1)}: abcd@efg.com`"
                                  :state="itemChanged && !valid ? false : null">
                    </b-form-input>

                    <div v-if="itemChanged && !valid" class="my-invalid-feedback"> <h6>{{ first(errors) }}</h6></div>
                  </b-input-group>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12">
              <h5> {{$tc('message.configuration', 1)}} {{$tc('message.function', 2)}} </h5>
            </b-col>

            <b-col cols="12">
              <explorer
                v-show="input.config && (!input.config.adminDefault || input.config.adminDefault === 'false' || input.config.adminDefault === 'undefined')"
                :collection="configs"
                :title-by="'title'"
                @clicked="handleClick($event)"
              />

              <h5 v-show="input.config && (input.config.adminDefault === 'true' || input.config.adminDefault === true)" class="c-green">
                {{$tc('info.allConfigsAreSelected', 1)}}
              </h5>
            </b-col>
          </b-row>

          <b-row class="mt-3">
            <b-col cols="auto" align-self="start" class="mr-auto">
              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-blue float-left mt-2"
                        v-if="userId && config.u && config.d"
                        :size="isMobile ? 'sm' : 'md'"
                        v-on:click="checkUser(false, input.config.disabled !== 'true')"
                        id="status-user"
                        :disabled="itemChanged">
                {{ input.config.disabled === 'true' ? $tc('message.enable', 2) : $tc('message.disable', 1)}}
              </b-button>
            </b-col>

            <b-col cols="auto" align-self="end" class="ml-auto">
              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2 mr-sm-2 mr-md-2 mr-lg-2 mr-xl-2 mt-2"
                        :size="isMobile ? 'sm' : 'md'"
                        v-on:click="checkUser(false, input.config.disabled)"
                        id="save-user"
                        v-if="userId ? config.u : config.c"
                        :disabled="invalid || !itemChanged">{{$t('message.save')}}
              </b-button>

              <!-- config[crud], create - read - update - delete -->
              <b-button class="b-c-green mr-2 mr-sm-2 mr-md-2 mr-lg-2 mr-xl-2 mt-2"
                        :size="isMobile ? 'sm' : 'md'"
                        v-on:click="checkUser(true, input.config.disabled)"
                        id="save-close-user"
                        v-if="userId ? config.u : config.c"
                        :disabled="invalid || !itemChanged">{{$t('message.saveClose')}}
              </b-button>

              <b-button class="b-c-green mr-0 mt-2"
                        :size="isMobile ? 'sm' : 'md'"
                        v-on:click="pushBack()"
                        id="close-user">{{$t('message.close')}}</b-button>
            </b-col>
          </b-row>
        </b-container>
      </validation-observer>
    </b-card>
  </b-container>
</template>

<script>
import { first, concat, cloneDeep, find } from 'lodash'
import Vue from 'vue'
import routerViews from '@/router-views'

export default {
  name: 'EditUser',
  created () {
    this.fetchData()
  },
  mounted () {
    this.$root.$on('change-lang', () => {
      this.$nextTick(() => {
        if (this && this.$refs && this.$refs.form) this.$refs.form.validate()
      })
      if (this.$el.isConnected) {
        this.getOptions()
      }
    })
    this.$root.$on('resize', (size) => {
      this.updateSize(size)
    })
    this.updateSize(Vue.myGetWindowSize())
  },
  data () {
    const username = this.$route.params.id !== 'new' ? this.$route.params.id : null
    const session = Vue.getLocalStorage('session')
    const user = session.user
    const config = this.$route.meta.config

    return {
      config,
      userId: username,
      isAdmin: user.config.role === 'Admin',
      isDefaultAdmin: user && user.config && user.config.adminDefault === 'true',
      first,
      configs: [],
      input: {
        role: 'Admin',
        fleets: [],
        config: {}
      },
      itemChanged: false,
      userTypeOptions: [],
      roleOptions: [],
      passwordCheck: null,
      watcher: null,
      size: null,
      isMobile: null,
      wasDefaultAdmin: false
    }
  },
  methods: {
    clickedLink (link) {
      this.pushBack(link)
    },
    updateSize (size) {
      this.size = size
      this.isMobile = size !== 'lg' && size !== 'xl'
    },
    checkWatcher () {
      this.$root.$emit('activeLoader', true)
      if (this.watcher) this.watcher()
      this.itemChanged = false
    },
    addWatcher () {
      this.$root.$emit('activeLoader', false)
      this.watcher = this.$watch('input', () => {
        this.itemChanged = true
      }, { deep: true })
    },
    fetchData () {
      this.checkWatcher()
      this.getOptions().then(() => {
        this.getUser(this.userId).then(() => {
          this.addWatcher()
        })
      })
    },
    getOptions () {
      const axiosArray = []
      this.getRoleOptions()
      axiosArray.push(this.getEnum('user_type', 'userTypeOptions'))
      return Vue.myAll(axiosArray).then(r => r)
    },
    getRoleOptions () {
      this.roleOptions = [
        { val: 'Admin', text: this.$tc('message.admin', 1) },
        { val: 'Operator', text: this.$tc('message.operator', 1) }
      ]
    },
    getConfigItems () {
      return routerViews.configItems(this)
    },
    getEnum (path, obj) {
      return Vue.myGet(this, `/enums/${path}`, 'data').then((items) => {
        const selection = [{ id: '', descr: items.length !== 0 ? this.$t('message.selectOption') : this.$t('message.noOption') }]
        const options = concat(selection, items)

        if (obj) this[obj] = options
        return options
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    getUser (id) {
      if (id) {
        return Vue.myGet(this, `/users/${id}`, 'users').then((users) => {
          const configItems = this.getConfigItems()
          this.input = first(users)
          if (this.input.config.adminDefault === "true") this.input.config.adminDefault = true
          else this.input.config.adminDefault = false
          this.wasDefaultAdmin = this.input && this.input.config && this.input.config.adminDefault

          const routes = this.input.config && this.input.config.routes ? JSON.parse(this.input.config.routes) : null
          const mergedRoutes = this.mergeConfigAndRoutes(configItems, routes)

          if (mergedRoutes) {
            this.input.config.routes = mergedRoutes
            this.configs = mergedRoutes
          } else {
            this.configs = this.getConfigItems()
            this.input.config.routes = routes || this.configs
          }
          return true
        }).catch((error) => {
          Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
          return false
        })
      } else {
        this.configs = this.getConfigItems()
      }
      return new Promise(resolve => resolve(true))
    },
    mergeConfigAndRoutes (configs, routes) {
      if (!routes) return configs
      configs.forEach((config, index) => {
        const foundedRoute = this.findRoute(config, routes)
        if (config.child && config.child.length !== 0) this.mergeConfigAndRoutes(config.child, routes)
        if (foundedRoute && foundedRoute.child && config.child && foundedRoute.child.length === config.child.length) {
          configs[index] = foundedRoute
        }
      })
      return configs
    },
    findRoute (item, routes) {
      if (!routes || routes.length === 0) return null
      const path = item.path

      let foundedItem = null
      routes.forEach(route => {
        if ((route.path === path && path) ||
          (route.title === item.title &&
            route.componentName === item.componentName && !path)) foundedItem = route
        else if (route.child && route.child.length !== 0 && !foundedItem) {
          foundedItem = this.findRoute(item, route.child)
        }
      })
      return foundedItem
    },
    checkUser (exit, userStatus) {
      const item = cloneDeep(this.input)
      const isDefaultAdmin = this.input && this.input.config && this.input.config.adminDefault === true

      if (!item.config) item.config = {}
      if (userStatus !== undefined) item.config.disabled = userStatus + ''
      if (!item.password) delete item.password

      item.config.routes = JSON.stringify(isDefaultAdmin ? this.getConfigItems() : this.configs)
      item.config.adminDefault = isDefaultAdmin + ''

      if (this.wasDefaultAdmin && !isDefaultAdmin) {
        this.checkDefaultAdmins().then(goAhead => {
          if (goAhead) {
            this.saveUser(exit, item)
          } else {
            Vue.warning(this, this.$tc('warnings.atLeastDefaultAdminNeeded', 1))
          }
        })
      } else {
        this.saveUser(exit, item)
      }
    },
    checkDefaultAdmins () {
      return Vue.myGet(this, '/users', 'users').then(users => {
        return !!find(users, obj => {
          return obj && obj.config && obj.config.adminDefault &&
            obj.config.adminDefault === 'true' && (!obj.disabled || obj.config.disabled === 'false') &&
            obj.userName !== this.userId
        })
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return false
      })
    },
    saveUser (exit, item) {
      const id = this.userId
      return Vue.putOrPost(this, !!id, `/users${id ? '/' + id : ''}`, item).then((r) => {
        Vue.success(this, `${this.$tc('message.user', 1)} ${this.$t(id ? 'success.putSuccess' : 'success.postSuccess')}`)
        if (exit) {
          this.itemChanged = false
          this.pushBack()
        } else this.$router.push({ name: 'RefreshPage', params: { id: r.data.userName, name: 'EditUser' } })
        return true
      }).catch(error => {
        Vue.manageErrors(this, error, Vue.getDimetiorHttpCode())
        return error
      })
    },
    pushBack (obj) {
      const backObj = obj || { name: 'User' }
      if (this.itemChanged) this.$root.$emit('go-back-modal', backObj)
      else this.$router.push(backObj)
    },
    handleClick () {
      this.itemChanged = true
    }
  }
}
</script>
